/* BUILDER B’s route registrations — see routes-a.tsx for the pattern.
   Screens live in src/routes/{parametres,aide,etablissements,admin}/.
   Scope: WF-13 · WF-14 · WF-15.

   Routes are registered explicitly, one per frame — App.tsx mounts every NAV
   path as `path + '/*'` → Placeholder ahead of this array, and a splat here
   would lose the ranking tie and render the placeholder instead. Explicit
   paths outrank it, and give each frame its own URL for evidence shots. */
import { Route } from 'react-router-dom'
import type { ReactElement } from 'react'
import AdminShell from '@/routes/admin/AdminShell'
import AD01Workspaces from '@/routes/admin/AD01Workspaces'
import AD02Entitlements from '@/routes/admin/AD02Entitlements'
import AD03Pilot from '@/routes/admin/AD03Pilot'
import AD04Disputes from '@/routes/admin/AD04Disputes'
import AD05Fraud from '@/routes/admin/AD05Fraud'
import AD06Guidance from '@/routes/admin/AD06Guidance'
import AD07EmailOps from '@/routes/admin/AD07EmailOps'
import AD08Platform from '@/routes/admin/AD08Platform'
import AD09Backups from '@/routes/admin/AD09Backups'
import AD10LaunchGates from '@/routes/admin/AD10LaunchGates'
import EtabShell from '@/routes/etablissements/EtabShell'
import ML01Configurer from '@/routes/etablissements/ML01Configurer'
import ML02Import from '@/routes/etablissements/ML02Import'
import ML03Groupes from '@/routes/etablissements/ML03Groupes'
import ML04Preparation from '@/routes/etablissements/ML04Preparation'
import ML05Vagues from '@/routes/etablissements/ML05Vagues'
import ML06Comparer from '@/routes/etablissements/ML06Comparer'
import ML07Appliquer from '@/routes/etablissements/ML07Appliquer'
import ML08Resultat from '@/routes/etablissements/ML08Resultat'
import ParamShell, { ParamIndex } from '@/routes/parametres/ParamShell'
import BI01Offre from '@/routes/parametres/BI01Offre'
import BI02Essai from '@/routes/parametres/BI02Essai'
import BI03Utilisation from '@/routes/parametres/BI03Utilisation'
import BI04Paiement from '@/routes/parametres/BI04Paiement'
import BI05Annuler from '@/routes/parametres/BI05Annuler'
import BI06Profil from '@/routes/parametres/BI06Profil'
import BI07Factures from '@/routes/parametres/BI07Factures'
import BI08Equipe from '@/routes/parametres/BI08Equipe'
import BI09Inviter from '@/routes/parametres/BI09Inviter'
import BI10Espace from '@/routes/parametres/BI10Espace'
import BI11Annuel from '@/routes/parametres/BI11Annuel'
import BI12Etablissement from '@/routes/parametres/BI12Etablissement'
import BI13Starter from '@/routes/parametres/BI13Starter'
import BI14Gestion from '@/routes/parametres/BI14Gestion'
import Compte from '@/routes/parametres/Compte'
import Integrations from '@/routes/parametres/Integrations'
import Plateformes from '@/routes/parametres/Plateformes'
import Organisation from '@/routes/parametres/Organisation'
import Equipe from '@/routes/equipe/Equipe'
import Caisse from '@/routes/equipe/Caisse'
import MonQr from '@/routes/equipe/MonQr'
import AideShell from '@/routes/aide/AideShell'
import AS01Aide from '@/routes/aide/AS01Aide'
import AS02Guide from '@/routes/aide/AS02Guide'
import AS03Contact from '@/routes/aide/AS03Contact'

export const routesB: ReactElement[] = [
  /* WF-15 · internal admin — AD-01…AD-10, static tier */
  <Route key="admin" path="/admin" element={<AdminShell />}>
    <Route index element={<AD01Workspaces />} />
    <Route path="entitlement" element={<AD02Entitlements />} />
    <Route path="pilote" element={<AD03Pilot />} />
    <Route path="litiges" element={<AD04Disputes />} />
    <Route path="fraude" element={<AD05Fraud />} />
    <Route path="guides" element={<AD06Guidance />} />
    <Route path="email" element={<AD07EmailOps />} />
    <Route path="plateforme" element={<AD08Platform />} />
    <Route path="sauvegardes" element={<AD09Backups />} />
    <Route path="portes" element={<AD10LaunchGates />} />
  </Route>,

  /* WF-14 · multi-location ops — ML-01…ML-08.
     /etablissements itself is the Orchestrator’s ON-18 screen on main, so the
     ML frames mount beneath it; the index is deliberately not claimed here. */
  <Route key="etab" path="/etablissements" element={<EtabShell />}>
    <Route path="configurer" element={<ML01Configurer />} />
    <Route path="import" element={<ML02Import />} />
    <Route path="groupes" element={<ML03Groupes />} />
    <Route path="preparation" element={<ML04Preparation />} />
    <Route path="vagues" element={<ML05Vagues />} />
    <Route path="comparer" element={<ML06Comparer />} />
    <Route path="appliquer" element={<ML07Appliquer />} />
    <Route path="appliquer/resultat" element={<ML08Resultat />} />
  </Route>,

  /* WF-13 · billing, team, settings & support — BI-01…BI-15 under /parametres
     (the manifest’s route; the bare /equipe nav item stays Placeholder until
     the Orchestrator wires it, per the ruling of 30/08). BI-15 is BI-08's
     empty state and shows in day-one. FQ-I4: the index opens Mon établissement, « Votre offre » is /parametres/offre. */
  <Route key="param" path="/parametres" element={<ParamShell />}>
    <Route index element={<ParamIndex />} />
    <Route path="offre" element={<BI01Offre />} />
    <Route path="essai" element={<BI02Essai />} />
    <Route path="utilisation" element={<BI03Utilisation />} />
    <Route path="paiement" element={<BI04Paiement />} />
    <Route path="annuler" element={<BI05Annuler />} />
    <Route path="profil" element={<BI06Profil />} />
    <Route path="factures" element={<BI07Factures />} />
    <Route path="equipe" element={<BI08Equipe />} />
    <Route path="equipe/inviter" element={<BI09Inviter />} />
    <Route path="equipe/gestion" element={<BI14Gestion />} />
    <Route path="espace" element={<BI10Espace />} />
    <Route path="offre/annuel" element={<BI11Annuel />} />
    <Route path="offre/starter" element={<BI13Starter />} />
    <Route path="etablissement" element={<BI12Etablissement />} />
    {/* batch 22 · Roadmap 0.4 — two of Paramètres' four pages (Scope v5 §4) */}
    <Route path="compte" element={<Compte />} />
    <Route path="integrations" element={<Integrations />} />
    {/* 08/10 · « Vos demandes » folded into the builder: its « Plateformes » tab is a Paramètres page (errata B1/B2) */}
    <Route path="plateformes" element={<Plateformes />} />
    {/* v3.3 WF-042 · « Organisation »: the workspace's étiquettes (ruling « Organisation », 19/10) */}
    <Route path="organisation" element={<Organisation />} />
  </Route>,

  /* batch 22 · Suivre › Équipe as one list (Scope v5 §8). App.tsx still
     registers /equipe → EquipeAccueil; an index route outranks a plain path
     of the same length in React Router's ranking, so this one serves until
     the Orchestrator removes that line (status doc). Builder A's
     /equipe/defi* routes are siblings in routes-a and are untouched. */
  <Route key="equipe" path="/equipe">
    <Route index element={<Equipe />} />
  </Route>,

  /* « Valider un cadeau », the door (gift-wallet review P0.3, MA-3, V19): the sidebar's and Accueil's, every role */
  <Route key="caisse" path="/caisse" element={<Caisse />} />,
  /* v3.3 WF-117/118/119 (pack equipe-qr) · the Collaborateur's own « Mon QR » (lib/nav MON_QR); App.tsx's stop-gap Redirect to Équipe goes */
  <Route key="mon-qr" path="/mon-qr" element={<MonQr />} />,

  /* WF-13 · Aide et support — AS-01…AS-03, new in v5 */
  <Route key="aide" path="/aide" element={<AideShell />}>
    <Route index element={<AS01Aide />} />
    <Route path="article" element={<AS02Guide />} />
    <Route path="contact" element={<AS03Contact />} />
  </Route>,
]
